import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { FeedProvider } from '@/contexts/FeedContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppShell from '@/components/AppShell';
import { getHomePath } from '@/lib/home';

// Critical-path pages — kept in main bundle (auth + the screen users land on first).
import AuthPage from '@/pages/Auth';
import CapturePage from '@/pages/Capture';
import FeedPage from '@/pages/Feed';
import ProfilePage from '@/pages/Profile';

// Lazy-loaded routes — split out of the main bundle. The biggest wins here are
// MarketplaceMap (~150 KB Leaflet) and EmoticonComposer (~250 KB Konva).
const ComposerPage = lazy(() => import('@/pages/Composer'));
const PostDetailPage = lazy(() => import('@/pages/PostDetail'));
const CommunitiesPage = lazy(() => import('@/pages/Communities'));
const CommunityDetailPage = lazy(() => import('@/pages/CommunityDetail'));
const ContestsPage = lazy(() => import('@/pages/Contests'));
const ContestCycleDetailPage = lazy(() => import('@/pages/ContestCycleDetail'));
const ContestSubmitPage = lazy(() => import('@/pages/ContestSubmit'));
const ContestAgreementPage = lazy(() => import('@/pages/ContestAgreement'));
const ContestWinnersPage = lazy(() => import('@/pages/ContestWinners'));
const MediaLibraryPage = lazy(() => import('@/pages/MediaLibrary'));
const MessagesPage = lazy(() => import('@/pages/Messages'));
const SettingsPage = lazy(() => import('@/pages/Settings'));
const WalletPage = lazy(() => import('@/pages/Wallet'));
const NotificationsPage = lazy(() => import('@/pages/Notifications'));
const SearchPage = lazy(() => import('@/pages/Search'));
const DiscoverPage = lazy(() => import('@/pages/Discover'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPassword'));
const DatingProfilePage = lazy(() => import('@/pages/DatingProfile'));
const ProfileEditPage = lazy(() => import('@/pages/ProfileEdit'));
const FriendsPage = lazy(() => import('@/pages/Friends'));
const AdminPage = lazy(() => import('@/pages/Admin'));
const CreatorAnalyticsPage = lazy(() => import('@/pages/CreatorAnalytics'));
const EmoticonComposerPage = lazy(() => import('@/pages/EmoticonComposer'));
const MarketplaceMapPage = lazy(() => import('@/pages/MarketplaceMap'));
const PaymentsReturnPage = lazy(() => import('@/pages/PaymentsReturn'));
const BlastPostPage = lazy(() => import('@/pages/BlastPost'));
const AdsDashboardPage = lazy(() => import('@/pages/AdsDashboard'));
import '@/App.css';

function RouteFallback() {
  return (
    <div className="mx-auto max-w-[720px] px-4 py-8 space-y-3" aria-busy="true" data-testid="route-suspense-fallback">
      <div className="h-8 w-1/3 animate-pulse rounded-full bg-[hsl(var(--app-card-2))]" />
      <div className="h-4 w-2/3 animate-pulse rounded-full bg-[hsl(var(--app-card-2))]" />
      <div className="h-44 animate-pulse rounded-2xl bg-[hsl(var(--app-card-2))]" />
      <div className="h-44 animate-pulse rounded-2xl bg-[hsl(var(--app-card-2))]" />
    </div>
  );
}

function RootLanding() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Phase 21 — single source of truth lives in lib/home. RootLanding,
  // AuthPage post-login, and VerifyEmail's "already verified" branches all
  // route through `getHomePath()` so a verified user's preferred tab is
  // honoured everywhere.
  return <Navigate to={getHomePath(user)} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <FeedProvider>
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            {/* Phase 23 — email verification REMOVED. The /verify-email
                route is now a hard redirect to root so any old verification
                links sitting in users' inboxes don't render a 404. */}
            <Route path="/verify-email" element={<Navigate to="/" replace />} />
            {/* Phase 20 — password reset routes are public because the URL
                token IS the credential, and the user is by definition
                unauthenticated when they click the email link from their
                inbox. */}
            <Route
              path="/forgot-password"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <ForgotPasswordPage />
                </Suspense>
              }
            />
            <Route
              path="/reset-password"
              element={
                <Suspense fallback={<RouteFallback />}>
                  <ResetPasswordPage />
                </Suspense>
              }
            />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path="/" element={<RootLanding />} />
                      <Route path="/capture" element={<CapturePage />} />
                      <Route path="/feed" element={<FeedPage />} />
                      <Route path="/feed/creator" element={<FeedPage />} />
                      <Route path="/feed/investor" element={<FeedPage />} />
                      <Route path="/feed/crowdfunder" element={<FeedPage />} />
                      <Route path="/feed/dating" element={<FeedPage />} />
                      <Route path="/feed/marketplace" element={<FeedPage />} />
                      <Route path="/feed/marketplace/map" element={<MarketplaceMapPage />} />
                      <Route path="/post/new" element={<ComposerPage />} />
                      <Route path="/post/:id" element={<PostDetailPage />} />
                      <Route path="/communities" element={<CommunitiesPage />} />
                      <Route path="/c/:slug" element={<CommunityDetailPage />} />
                      <Route path="/contests" element={<ContestsPage />} />
                      <Route path="/contests/winners" element={<ContestWinnersPage />} />
                      <Route path="/contests/cycle/:cycleId" element={<ContestCycleDetailPage />} />
                      <Route path="/contests/cycle/:cycleId/submit" element={<ContestSubmitPage />} />
                      <Route path="/contests/cycle/:cycleId/agreement" element={<ContestAgreementPage />} />
                      <Route path="/messages" element={<MessagesPage />} />
                      <Route path="/messages/:id" element={<MessagesPage />} />
                      <Route path="/u/:username" element={<ProfilePage />} />
                      <Route path="/me" element={<ProfilePage self />} />
                      <Route path="/profile/edit" element={<ProfileEditPage />} />
                      <Route path="/friends" element={<FriendsPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="/wallet" element={<WalletPage />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route path="/search" element={<SearchPage />} />
                      <Route path="/discover" element={<DiscoverPage />} />
                      <Route path="/dating/profile" element={<DatingProfilePage />} />
                      <Route path="/emoticons" element={<EmoticonComposerPage />} />
                      <Route path="/admin" element={<AdminPage />} />
                      <Route path="/analytics" element={<CreatorAnalyticsPage />} />
                      <Route path="/payments/return" element={<PaymentsReturnPage />} />
                      <Route path="/ads" element={<AdsDashboardPage />} />
                      <Route path="/post/:postId/blast" element={<BlastPostPage />} />
                      <Route path="/library" element={<MediaLibraryPage />} />
                      <Route path="/u/:username/library" element={<MediaLibraryPage />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </Suspense>
                  </AppShell>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster richColors position="top-right" />
          </FeedProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
